import type { ReactElement } from 'react';

const FLOWER_COLORS = ['var(--accent)', 'var(--warning)', 'var(--danger)'];

/**
 * The wide margins either side of the page, dressed as the garden and the
 * wall of the big house the app itself lives inside. Drawn once, for the
 * left edge; the right edge is the same artwork mirrored in CSS
 * (`.page-backdrop__side--right { transform: scaleX(-1) }`), so the house
 * wall always sits on the inner edge, next to the content, on both sides.
 */
export function PageBackdrop(): ReactElement {
  return (
    <div className="page-backdrop" aria-hidden="true">
      <div className="page-backdrop__side page-backdrop__side--left">
        <HouseGardenStrip />
      </div>
      <div className="page-backdrop__side page-backdrop__side--right">
        <HouseGardenStrip />
      </div>
    </div>
  );
}

function HouseGardenStrip(): ReactElement {
  return (
    <svg viewBox="0 0 200 900" preserveAspectRatio="xMidYMax slice">
      {/* garden ground */}
      <rect x={0} y={600} width={200} height={300} fill="var(--inn-garden-ground)" />

      {/* a gravel path leading toward the house */}
      <Path />

      {/* the house wall, running the full height along the inner edge */}
      <rect
        x={150}
        y={90}
        width={56}
        height={820}
        fill="var(--surface-sunken)"
        stroke="var(--border)"
        strokeWidth={2}
      />
      <path d="M138 90 L206 26 L206 90 Z" fill="var(--border-strong)" />
      <rect
        x={164}
        y={250}
        width={28}
        height={38}
        rx={3}
        fill="var(--inn-window-sky)"
        stroke="var(--border-strong)"
        strokeWidth={2.5}
      />
      <line x1={178} y1={250} x2={178} y2={288} stroke="var(--border-strong)" strokeWidth={2} />
      <line x1={164} y1={269} x2={192} y2={269} stroke="var(--border-strong)" strokeWidth={2} />
      <rect
        x={164}
        y={430}
        width={28}
        height={38}
        rx={3}
        fill="var(--inn-window-sky)"
        stroke="var(--border-strong)"
        strokeWidth={2.5}
      />
      <line x1={178} y1={430} x2={178} y2={468} stroke="var(--border-strong)" strokeWidth={2} />
      <line x1={164} y1={449} x2={192} y2={449} stroke="var(--border-strong)" strokeWidth={2} />

      {/* a tree, a run of shrubs along the wall, and a few loose flowers */}
      <Tree cx={44} baseY={700} />
      <Bush cx={110} cy={680} r={22} />
      <Bush cx={135} cy={655} r={17} />
      <Bush cx={30} cy={760} r={20} />
      <Bush cx={125} cy={810} r={24} />
      <Bush cx={70} cy={840} r={19} />
      <Flowers />
    </svg>
  );
}

function Path(): ReactElement {
  const spots = [
    { x: 30, y: 860 },
    { x: 55, y: 815 },
    { x: 82, y: 775 },
    { x: 108, y: 735 },
    { x: 132, y: 700 },
  ];
  return (
    <>
      {spots.map((spot, index) => (
        <ellipse
          key={index}
          cx={spot.x}
          cy={spot.y}
          rx={14}
          ry={8}
          fill="var(--border-strong)"
          opacity={0.35}
        />
      ))}
    </>
  );
}

function Tree({ cx, baseY }: { cx: number; baseY: number }): ReactElement {
  return (
    <>
      <rect x={cx - 5} y={baseY} width={10} height={90} rx={3} fill="var(--border-strong)" />
      <circle cx={cx - 20} cy={baseY - 10} r={30} fill="var(--inn-garden-leaf-soft)" />
      <circle cx={cx + 22} cy={baseY - 8} r={28} fill="var(--inn-garden-leaf-soft)" />
      <circle cx={cx} cy={baseY - 42} r={34} fill="var(--inn-garden-leaf)" />
    </>
  );
}

function Bush({ cx, cy, r }: { cx: number; cy: number; r: number }): ReactElement {
  return (
    <>
      <circle cx={cx - r * 0.6} cy={cy + r * 0.2} r={r * 0.7} fill="var(--inn-garden-leaf-soft)" />
      <circle cx={cx + r * 0.6} cy={cy + r * 0.2} r={r * 0.7} fill="var(--inn-garden-leaf-soft)" />
      <circle cx={cx} cy={cy} r={r} fill="var(--inn-garden-leaf)" />
    </>
  );
}

function Flowers(): ReactElement {
  const dots = [
    { x: 88, y: 700 },
    { x: 96, y: 712 },
    { x: 20, y: 730 },
    { x: 145, y: 790 },
    { x: 152, y: 820 },
    { x: 58, y: 862 },
  ];
  return (
    <>
      {dots.map((dot, index) => (
        <circle
          key={index}
          cx={dot.x}
          cy={dot.y}
          r={3}
          fill={FLOWER_COLORS[index % FLOWER_COLORS.length]}
        />
      ))}
    </>
  );
}
